import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumbs from '@/components/base/Breadcrumbs';
import SEOMeta from '@/components/base/SEOMeta';
import JsonLd from '@/components/base/JsonLd';
import { aboutData } from '@/mocks/about';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    setFormError('');

    const form = e.currentTarget;
    const honeypot = form.querySelector<HTMLInputElement>('input[name="phone_alt"]');
    if (honeypot && honeypot.value.trim() !== '') {
      setFormStatus('success');
      setSubmitting(false);
      return;
    }

    try {
      const formData = new FormData(form);
      if (honeypot) formData.delete('phone_alt');

      const response = await fetch('https://readdy.ai/api/form/d9u452lca23r0tr1aq70', {
        method: 'POST',
        body: new URLSearchParams(formData as unknown as URLSearchParams).toString(),
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '提交失败，请稍后重试';
        if (typeof serverMsg === 'string' && serverMsg.toLowerCase().includes('spam')) {
          setFormStatus('success');
        } else {
          setFormError(typeof serverMsg === 'string' ? serverMsg : '提交失败，请稍后重试');
          setFormStatus('error');
        }
      }
    } catch {
      setFormError('网络错误，请检查网络连接后重试');
      setFormStatus('error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <ScrollToTop />
      <SEOMeta
        title="关于我们 - 蜜桃网站 | 精选内容聚合平台"
        description="了解蜜桃网站的品牌故事、核心价值与团队理念。蜜桃网站致力于为用户提供高质量的内容聚合服务，覆盖科技、娱乐、生活、美食、健康、财经等多元领域。"
        keywords="蜜桃网站,关于蜜桃网站,蜜桃网站介绍,蜜桃网站联系方式,内容聚合平台"
        canonicalUrl="https://weifutongmicropay.cn/about"
      />
      <JsonLd
        data={{
          '@context': 'https://schema.org',
          '@type': 'AboutPage',
          name: '关于蜜桃网站',
          description: '了解蜜桃网站的品牌故事、核心价值与团队理念。',
          url: 'https://weifutongmicropay.cn/about',
          isPartOf: {
            '@type': 'WebSite',
            name: '蜜桃网站',
            url: 'https://weifutongmicropay.cn',
          },
        }}
      />
      <JsonLd
        data={{
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: '首页', item: 'https://weifutongmicropay.cn' },
            { '@type': 'ListItem', position: 2, name: '关于我们', item: 'https://weifutongmicropay.cn/about' },
          ],
        }}
      />
      <JsonLd
        data={{
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: [
            {
              '@type': 'Question',
              name: '蜜桃网站是什么？',
              acceptedAnswer: {
                '@type': 'Answer',
                text: '蜜桃网站是国内领先的精选内容聚合平台，为用户提供科技数码、娱乐影视、生活方式、美食旅行、健康养生、财经商业等多元化的优质内容。',
              },
            },
            {
              '@type': 'Question',
              name: '蜜桃网站的内容是否免费？',
              acceptedAnswer: {
                '@type': 'Answer',
                text: '是的，蜜桃网站所有内容完全免费阅读。我们致力于让优质内容触手可及，无需付费即可畅读所有文章。',
              },
            },
            {
              '@type': 'Question',
              name: '如何联系蜜桃网站？',
              acceptedAnswer: {
                '@type': 'Answer',
                text: '您可以通过邮件 contact@weifutongmicropay.cn、电话 400-888-6666 联系我们的客服团队，或直接在本页面留言，我们会在1-2个工作日内回复。',
              },
            },
            {
              '@type': 'Question',
              name: '蜜桃网站的内容多久更新一次？',
              acceptedAnswer: {
                '@type': 'Answer',
                text: '蜜桃网站每天更新精选内容，覆盖各个分类频道。我们的编辑团队全天候工作，确保为您提供最新、最有价值的文章。',
              },
            },
          ],
        }}
      />

      <Navbar />

      <main className="pt-16">
        {/* Page Header */}
        <section className="py-10 md:py-14 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="mb-4">
              <Breadcrumbs items={[{ label: '首页', href: '/' }, { label: '关于我们' }]} />
            </div>
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-900 mb-3">
              关于蜜桃网站
            </h1>
            <p className="text-sm md:text-base text-foreground-600 max-w-2xl">
              精选内容聚合平台，海量资源一站直达。我们致力于成为您发现优质内容的首选伙伴。
            </p>
          </div>
        </section>

        {/* Stats */}
        <section className="py-8 md:py-10 bg-background-50 border-b border-background-200">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {aboutData.stats.map((stat) => (
                <div key={stat.label} className="text-center">
                  <p className="text-2xl md:text-3xl font-heading font-bold text-primary-500">{stat.value}</p>
                  <p className="text-sm text-foreground-600 mt-1">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Brand Story */}
        <section className="py-10 md:py-14 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
              <div>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-5">
                  品牌故事
                </h2>
                <div className="text-sm md:text-base text-foreground-700 leading-relaxed space-y-4">
                  {aboutData.brandStory.split('\n\n').map((paragraph, i) => (
                    <p key={i}>{paragraph}</p>
                  ))}
                </div>
              </div>
              <div className="aspect-[4/3] rounded-lg overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20creative%20workspace%20with%20warm%20lighting%2C%20team%20collaboration%20atmosphere%2C%20clean%20minimalist%20office%20design%2C%20plants%20and%20wooden%20elements%2C%20cozy%20and%20professional%20environment&width=800&height=600&seq=about-brand&orientation=landscape"
                  alt="蜜桃网站团队"
                  className="w-full h-full object-cover"
                  title="蜜桃网站 - 品牌故事"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Core Values */}
        <section className="py-10 md:py-14 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 text-center mb-10">
              核心价值观
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
              {aboutData.values.map((value) => (
                <div key={value.title} className="p-6 rounded-lg bg-background-50 border border-background-200/70">
                  <span className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 text-accent-700 text-lg mb-4">
                    <i className={value.icon}></i>
                  </span>
                  <h3 className="text-base font-semibold text-foreground-900 mb-2">{value.title}</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">{value.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="py-10 md:py-14 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 text-center mb-3">
              用户口碑
            </h2>
            <p className="text-sm text-foreground-600 text-center mb-10 max-w-xl mx-auto">
              听听我们的用户怎么说
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
              {aboutData.testimonials.map((t) => (
                <div key={t.name} className="p-6 rounded-lg bg-background-100 border border-background-200/70">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0">
                      <img
                        src={t.avatar}
                        alt={t.name}
                        className="w-full h-full object-cover"
                        title={t.name}
                      />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground-900">{t.name}</p>
                      <p className="text-xs text-foreground-500">{t.role}</p>
                    </div>
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed">&ldquo;{t.content}&rdquo;</p>
                  <div className="flex gap-0.5 mt-3">
                    {[...Array(5)].map((_, i) => (
                      <i key={i} className="ri-star-fill text-accent-400 text-xs"></i>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Form */}
        <section className="py-10 md:py-14 bg-background-100">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 text-center mb-3">
              联系我们
            </h2>
            <p className="text-sm text-foreground-600 text-center mb-8">
              有任何问题或合作意向？请通过以下方式联系我们，或直接填写表单留言。
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
              <div className="flex flex-col items-center gap-2 p-4 rounded-lg bg-background-50 border border-background-200/70">
                <span className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 text-accent-700">
                  <i className="ri-mail-line"></i>
                </span>
                <p className="text-xs text-foreground-500">邮箱</p>
                <p className="text-sm font-medium text-foreground-900">{aboutData.contactInfo.email}</p>
              </div>
              <div className="flex flex-col items-center gap-2 p-4 rounded-lg bg-background-50 border border-background-200/70">
                <span className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 text-accent-700">
                  <i className="ri-phone-line"></i>
                </span>
                <p className="text-xs text-foreground-500">电话</p>
                <p className="text-sm font-medium text-foreground-900">{aboutData.contactInfo.phone}</p>
              </div>
              <div className="flex flex-col items-center gap-2 p-4 rounded-lg bg-background-50 border border-background-200/70">
                <span className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 text-accent-700">
                  <i className="ri-map-pin-line"></i>
                </span>
                <p className="text-xs text-foreground-500">地址</p>
                <p className="text-sm font-medium text-foreground-900 text-center">{aboutData.contactInfo.address}</p>
              </div>
            </div>

            <form
              onSubmit={handleSubmit}
              data-readdy-form="true"
              className="space-y-4"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    姓名 <span className="text-primary-500">*</span>
                  </label>
                  <input
                    id="contact-name"
                    name="name"
                    type="text"
                    required
                    className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all"
                    placeholder="请输入您的姓名"
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    邮箱 <span className="text-primary-500">*</span>
                  </label>
                  <input
                    id="contact-email"
                    name="email"
                    type="email"
                    required
                    className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all"
                    placeholder="请输入您的邮箱"
                  />
                </div>
              </div>
              <div>
                <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  主题
                </label>
                <input
                  id="contact-subject"
                  name="subject"
                  type="text"
                  className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all"
                  placeholder="请输入主题"
                />
              </div>
              <div>
                <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  留言内容 <span className="text-primary-500">*</span>
                </label>
                <textarea
                  id="contact-message"
                  name="message"
                  required
                  maxLength={500}
                  rows={5}
                  className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all resize-none"
                  placeholder="请输入您的留言内容（最多500字）"
                ></textarea>
                <p className="text-xs text-foreground-500 mt-1">最多500个字符</p>
              </div>

              <input
                type="text"
                name="phone_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="sr-only-honeypot"
              />

              <div className="flex flex-col sm:flex-row items-center gap-3 pt-2">
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full sm:w-auto px-8 py-3 bg-primary-500 text-background-50 rounded-full font-medium text-sm hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 whitespace-nowrap cursor-pointer"
                >
                  {submitting ? '提交中...' : '发送留言'}
                </button>
                {formStatus === 'success' && (
                  <p className="text-sm text-green-600 font-medium">感谢您的留言，我们会尽快回复！</p>
                )}
                {formStatus === 'error' && (
                  <p className="text-sm text-red-600 font-medium">{formError}</p>
                )}
              </div>
            </form>
          </div>
        </section>

        {/* CTA */}
        <section className="py-12 md:py-16 bg-background-50">
          <div className="max-w-3xl mx-auto px-4 md:px-6 text-center">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-3">
              加入蜜桃网站，发现更多精彩
            </h2>
            <p className="text-sm text-foreground-600 mb-6">
              每天都有新内容，随时随地畅享优质阅读体验。
            </p>
            <Link
              to="/"
              className="inline-flex px-8 py-3 bg-primary-500 text-background-50 rounded-full font-medium text-sm hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              返回首页开始探索
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}